import React from 'react';
import { Button, Divider } from 'antd';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Form } from 'formik-antd';

import SubmitButton from 'components/formik/SubmitButton';
import {
  getAssignmentBulkImportTemplate,
  preUploadBulkImportAssignments,
} from 'redux/reducers/assignmentSlice';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { useAppDispatch } from 'hooks/useAppDispatch';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import { ResourceDownloadResponse } from 'api/backofficeApiClient';
import AssignmentBulkImportUploader from '../AssignmentBulkImportUploader';
import { downloadFile } from 'utils/fileUtils';
import { AssignmentBulkImportViewModel } from 'api/adminApiClient';

import styles from '../AssignmentBulkImportPage.module.less';

const validationSchema = Yup.object({
  file: Yup.array().min(1, 'Field is required').required('Field is required'),
});

interface AssignmentManualFileModel {
  fileName?: string;
  data?: any;
}

export interface FormAssignmentBulkImport {
  file: AssignmentManualFileModel[];
}

const initialValues: FormAssignmentBulkImport = { file: [] };

type Props = {
  onSuccess: (customers: AssignmentBulkImportViewModel[]) => void;
};

export default function AssignmentUploadSection(props: Props) {
  const { onSuccess } = props;
  const dispatch = useAppDispatch();

  const downloadAssignmentFile = async (
    fileOptions: ResourceDownloadResponse,
  ) => {
    try {
      await downloadFile({
        downloadUrl: fileOptions.downloadUrl,
        fileName: fileOptions.fileName,
      });
    } catch (e: any) {
      BaseAlert.error(e.message);
    }
  };

  const handleDownloadTemplate = async () => {
    const result = await dispatch(getAssignmentBulkImportTemplate());

    if (getAssignmentBulkImportTemplate.fulfilled.match(result)) {
      await downloadAssignmentFile(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleSubmitError = (result: any) => {
    if (result.payload?.errorMessage || result.payload?.errorDetails) {
      BaseAlert.error(result.payload?.errorMessage || ApiErrorMessage);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const onSubmit = async (values: FormAssignmentBulkImport) => {
    const data = values.file[0];
    const result = await dispatch(preUploadBulkImportAssignments(data));

    if (preUploadBulkImportAssignments.fulfilled.match(result)) {
      await onSuccess(result.payload);
    } else {
      handleSubmitError(result);
    }
  };

  return (
    <>
      <BasePageLabel label="Upload file" />

      <div className="page-box page-inner">
        <div className="page-inner-container">
          <div className={styles.wrapper}>
            <div className={styles.box}>
              <div>
                1. Please download the template which contains all the fields
                required for creating Assignment records in bulk. Click on
                'Download Template' to begin the download process.
              </div>

              <div>
                <Button
                  className="with-icon"
                  onClick={handleDownloadTemplate}
                  icon={<span className="icon icon-download" />}
                >
                  Download template
                </Button>
              </div>
            </div>
            <Divider />
            <div className={styles.box}>
              <div>2. Upload Instructions:</div>
              <ul>
                <li>
                  <strong>Mandatory fields</strong>: Pay attention to the
                  asterisk sign (*) in the column headers. This means the column
                  is mandatory. Leaving it blank may result in failed creation.
                </li>
                <li>
                  <strong>Validation Rules</strong>: The validation rules on the
                  columns should be met in order to create the assignments. See
                  the description of each rule in the column comments.
                </li>
                <li>
                  Make use of the drop-down lists that contain items for
                  different columns such as Assignment type, Country,
                  Anticipated schedule, Job category, etc.
                </li>
                <li>
                  Make sure to fill in all the fields accurately and completely.
                </li>
                <li>
                  Save the file and go back to the {''}
                  <strong>Bulk import page.</strong>
                </li>
                <li>
                  Upload the file from your computer and click {''}
                  <strong>Next.</strong>
                </li>
              </ul>
            </div>
            <Divider />
            <div className={styles.box}>
              <div>3. Upload the File</div>
              <div>Select the file you want to upload from your computer.</div>

              <div className="upload-wrapper">
                <Formik
                  enableReinitialize
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={onSubmit}
                >
                  <Form layout="vertical">
                    <AssignmentBulkImportUploader fieldName="file" />
                    <div>
                      <SubmitButton className="with-icon">
                        Next <span className="icon icon-double-right" />
                      </SubmitButton>
                    </div>
                  </Form>
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
